/**
 * DrawNotificationTemplateEditor — admin-editable per-event template for
 * the "draw finalized" notification (in-app + email). Subject + body
 * accept the documented {{variable}} vocabulary; unknown placeholders are
 * left literal so typos are visible at preview time.
 *
 * Rendered inside RodeoDrawManagement under the header — admins can edit
 * at any time, but the Send button (separate, also in the header) is
 * only active once the draw is finalized so the list stays private until
 * the admin explicitly dispatches.
 */

import { useEffect, useState } from 'react';
import { Eye, Loader2, RotateCcw, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useDrawNotificationTemplate,
  useSaveDrawNotificationTemplate,
  usePreviewDrawNotificationTemplate,
  useSendDrawNotifications,
  type DrawNotificationPreviewResult,
} from '@/api/extensions/useDrawOps';
import { useToast } from '@/contexts/ToastContext';

interface Props {
  eventId: string;
  // True when the draw is finalized, which is the only state in which
  // sending is allowed. Drives the visibility of the "Send with edits
  // (one-off)" button — without this flag the editor would offer a
  // button that the backend would reject anyway.
  canSend?: boolean;
  className?: string;
}

export function DrawNotificationTemplateEditor({ eventId, canSend = false, className }: Props) {
  const { showSuccess, showError } = useToast();
  const { data, isLoading, error, refetch } = useDrawNotificationTemplate(eventId);
  const saveMutation = useSaveDrawNotificationTemplate();
  const previewMutation = usePreviewDrawNotificationTemplate();
  const sendMutation = useSendDrawNotifications();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<DrawNotificationPreviewResult | null>(null);
  // "html" shows the fully-wrapped HighCall email in a sandboxed iframe;
  // "text" shows the plain-text fallback that text-only mail clients get.
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');
  // Confirmation gate for the one-off send. Kept separate from the
  // header's "Send notifications" dialog because the copy is different
  // (this version warns the org template won't be updated).
  const [sendOpen, setSendOpen] = useState(false);
  // Track whether the user has typed anything since the last server sync.
  // Without this the textareas would never repopulate when the query
  // refetches (we'd be overwriting in-progress edits on every refetch).
  const [serverVersion, setServerVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const stamp = `${data.subject_template} ${data.body_template}`;
    if (stamp !== serverVersion) {
      setSubject(data.subject_template);
      setBody(data.body_template);
      setServerVersion(stamp);
    }
  }, [data, serverVersion]);

  const dirty =
    !!data &&
    (subject !== data.subject_template || body !== data.body_template);

  const handleSave = async () => {
    try {
      await saveMutation.mutateAsync({
        eventId,
        subject_template: subject,
        body_template: body,
      });
      await refetch();
      showSuccess('Template saved', 'Will be used the next time you send.');
    } catch (e: unknown) {
      showError(
        'Save failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const handleReset = () => {
    if (!data) return;
    // "Reset to default" only resets the in-progress edit back to whatever
    // the server returned — which IS the seed default when is_default=true.
    // To wipe a saved row back to the default, the admin would need to
    // type the default subject + body and save (next session: explicit
    // delete endpoint).
    setSubject(data.subject_template);
    setBody(data.body_template);
  };

  const handlePreview = async () => {
    try {
      // Pass in-progress edits so the admin previews exactly what's in the
      // textareas right now — saving first would force an extra round trip
      // for what is fundamentally a read-only "what will this look like?"
      // question.
      const result = await previewMutation.mutateAsync({
        eventId,
        subject_template: subject || undefined,
        body_template: body || undefined,
      });
      setPreview(result);
      setPreviewOpen(true);
    } catch (e: unknown) {
      showError(
        'Preview failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const handleSendWithEdits = async () => {
    setSendOpen(false);
    try {
      const result = await sendMutation.mutateAsync({
        eventId,
        subject_template: subject,
        body_template: body,
      });
      const summary = [
        `${result.inapp_sent} in-app`,
        `${result.email_sent} email`,
        ...(result.email_skipped_optout > 0 ? [`${result.email_skipped_optout} opted out`] : []),
        ...(result.email_failed > 0 ? [`${result.email_failed} failed`] : []),
      ].join(' · ');
      showSuccess(
        'Sent with one-off edits',
        `${summary} · Org template unchanged.`,
      );
      await refetch();
    } catch (e: unknown) {
      showError(
        'Send failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  if (isLoading) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading notification template…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={className}>
        <div className="text-sm text-destructive">
          Failed to load notification template
          {error instanceof Error ? `: ${error.message}` : ''}.
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className ?? ''}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold">
            Notification template
            {data.is_default && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                (default — not yet customised)
              </span>
            )}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Subject + body for the email and in-app message contestants
            receive when you send. <strong>Shared across every rodeo &amp; event</strong>{' '}
            under this organization. The default body is a scannable
            bold-label list (Event / Rodeo / Performance / When /
            Contestant / Draw position). Edit the {`{{variables}}`} below as
            needed, or use{' '}
            <em>Send with edits</em> to dispatch a one-off message without
            changing the org-wide default.
          </p>
        </div>
        {data.last_sent_at && (
          <div className="text-xs text-muted-foreground">
            Last sent:{' '}
            <time dateTime={data.last_sent_at}>
              {new Date(data.last_sent_at).toLocaleString()}
            </time>{' '}
            {typeof data.last_sent_count === 'number' && (
              <>({data.last_sent_count} recipients)</>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr_240px]">
        <div className="space-y-3">
          <div>
            <Label htmlFor="notif-subject" className="text-xs">
              Subject
            </Label>
            <Textarea
              id="notif-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              rows={2}
              className="font-mono text-sm"
            />
          </div>
          <div>
            <Label htmlFor="notif-body" className="text-xs">
              Body
            </Label>
            <Textarea
              id="notif-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="font-mono text-sm"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!dirty || saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1.5" />
              )}
              Save template
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleReset}
              disabled={!dirty || saveMutation.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Discard edits
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handlePreview}
              disabled={previewMutation.isPending}
            >
              {previewMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-1.5" />
              )}
              Preview
            </Button>
            {canSend && dirty && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSendOpen(true)}
                disabled={sendMutation.isPending}
              >
                {sendMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-1.5" />
                )}
                Send with edits (this event only)
              </Button>
            )}
          </div>
        </div>

        <aside className="rounded-md border bg-muted/30 p-3 text-xs max-h-[520px] overflow-y-auto">
          <div className="font-semibold mb-2">Available variables</div>
          <dl className="space-y-1.5">
            {data.variables.map((v) => (
              <div key={v.name}>
                <dt>
                  <code className="rounded bg-muted px-1 py-0.5 text-[10px]">
                    {`{{${v.name}}}`}
                  </code>
                </dt>
                <dd className="text-muted-foreground text-[11px] mt-0.5">
                  {v.description}
                </dd>
              </div>
            ))}
          </dl>
          {data.conditionals.length > 0 && (
            <>
              <div className="font-semibold mt-3 mb-2 pt-2 border-t border-border">
                Conditional sections
              </div>
              <p className="text-muted-foreground text-[11px] mb-2 leading-relaxed">
                Wrap text in <code className="rounded bg-muted px-1 py-0.5 text-[10px]">{'{{#name}}…{{/name}}'}</code> to only include
                it when the flag is true. Variables inside a removed section are never resolved.
              </p>
              <dl className="space-y-1.5">
                {data.conditionals.map((c) => (
                  <div key={c.name}>
                    <dt>
                      <code className="rounded bg-muted px-1 py-0.5 text-[10px]">
                        {`{{#${c.name}}}…{{/${c.name}}}`}
                      </code>
                    </dt>
                    <dd className="text-muted-foreground text-[11px] mt-0.5">
                      {c.description}
                    </dd>
                  </div>
                ))}
              </dl>
            </>
          )}
          <div className="font-semibold mt-3 mb-2 pt-2 border-t border-border">
            Formatting
          </div>
          <p className="text-muted-foreground text-[11px] leading-relaxed">
            Wrap text in{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-[10px]">
              **double asterisks**
            </code>{' '}
            to render it <strong>bold</strong> in the email — the default
            body uses this for its label/value list (e.g.{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-[10px]">
              {`**Event:** {{event_name}}`}
            </code>
            ). A blank line starts a new paragraph.
          </p>
        </aside>
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send with these edits — this event only?</DialogTitle>
            <DialogDescription>
              These in-progress edits will be used to send notifications
              for <strong>this event only</strong>. The org-wide template
              will <strong>not</strong> be updated — every other rodeo and
              event under this organization will keep using the saved
              template.
              <span className="block mt-2 text-xs text-muted-foreground">
                Want to update the org template instead? Click Cancel,
                then Save template.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSendOpen(false)}
              disabled={sendMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSendWithEdits} disabled={sendMutation.isPending}>
              {sendMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1.5" />
              )}
              Send (one-off)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Notification preview</DialogTitle>
            <DialogDescription>
              Rendered with real event/rodeo/org data and a fake contestant
              ("John Smith"). Shown inside the standard HighCall email
              wrapper — this is byte-for-byte what contestants will receive.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="flex flex-col gap-3 overflow-hidden">
              <div className="rounded border bg-muted/30 px-3 py-2 text-xs">
                <span className="font-semibold">Subject: </span>
                <span className="font-mono">{preview.subject}</span>
              </div>
              <div className="flex gap-1.5 text-xs">
                <Button
                  size="sm"
                  variant={previewMode === 'html' ? 'default' : 'outline'}
                  onClick={() => setPreviewMode('html')}
                >
                  HTML email
                </Button>
                <Button
                  size="sm"
                  variant={previewMode === 'text' ? 'default' : 'outline'}
                  onClick={() => setPreviewMode('text')}
                >
                  Plain text fallback
                </Button>
              </div>
              {previewMode === 'html' ? (
                <iframe
                  // Sandboxed iframe so the email's inline CSS can't leak
                  // into the host app, and so any malicious content (which
                  // shouldn't exist — backend escapes — but defense in depth)
                  // can't execute scripts or navigate the parent.
                  sandbox=""
                  title="Notification preview"
                  srcDoc={preview.body_html}
                  className="w-full min-h-[520px] flex-1 rounded border bg-white"
                />
              ) : (
                <pre className="flex-1 overflow-auto rounded border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap min-h-[520px]">
                  {preview.body_text}
                </pre>
              )}
              <div className="text-[11px] text-muted-foreground">
                Sample data used: John Smith · {preview.sample_context.event_name} · {preview.sample_context.rodeo_name} · {preview.sample_context.performance_name} · draw #{preview.sample_context.draw_position} · {preview.sample_context.payment_status}.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
